import React, { useEffect, useState } from 'react';
import GameMenu from '@base/features/game/components/gameMenu/components/gameMenu';
import { container } from 'tsyringe';
import GameBoard from '../../model/gameBoard';
import { Loader } from '@ui-kit/components/loader';
import ModalOpponentIdleActions from '@base/features/modal/components/modalOpponentIdleActions';
import { Subscription } from 'rxjs';
import GameButtons from '../gameButtons/gameButtons';
import { GameState } from '../../model/interfaces';
import MovesWindow from '../movesWindow';
import GameChatLayout from '../gameChatLayout/gameChatLayout';
import StatusMessage from '../statusMessage';
import { useBehaviorSubject, useSubject } from '@base/utils/helpers';
import { IPreferencesService } from '@base/features/preferences/model/types';
import Sizes from '@backgammon/model/sizes';
import { Helmet } from 'react-helmet';
import ogImage from '@ui-kit/images/register-bg.jpg';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { Preferences } from '@backgammon/constants/defaultPreferences';
import ImageProvider from '@base/features/image/imageProvider';
import { BackgammonImageCollectionKeys } from '@backgammon/constants/imageCollection';
import Color from '@base/features/game/model/color';
import PlayerInfo from '@base/features/game/components/playerInfo/playerInfo';
import blackChecker from '@backgammon/assets/checker_black.png';
import whiteChecker from '@backgammon/assets/checker_white.png';

export default function GameBoardPage() {
  const [game, setGame] = useState<GameBoard>();
  const [loading, setLoading] = useState(false);
  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const imageProvider: ImageProvider<BackgammonImageCollectionKeys> = container.resolve('imageProvider');
  const preferencesService: IPreferencesService<Preferences> = container.resolve('preferencesService');
  const preferences = useBehaviorSubject(preferencesService.onPreferencesChanged);
  const [opponenIdleActionsDialog, setOpponentIdleActionsDialog] = useState<boolean>(false);

  useEffect(() => {
    initGameState(
      playersOnlineRepository,
      imageProvider,
      setGame,
      preferences.BOARD,
      preferencesService,
      loading,
      setLoading
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences.BOARD]);

  useEffect(() => {
    let subscribers: Subscription[];
    if (game?.gameState) {
      subscribers = [
        game.gameState.opponentIdleActionsDialog.visible.subscribe(setOpponentIdleActionsDialog),
      ];
    }
    if (game)
      return () => {
        subscribers.forEach((s) => s.unsubscribe());
        game.destroy();
      };
  }, [game]);

  return (
    <div className="game-board-page">
      <Helmet>
        <meta property="og:title" content="Backgamonn Live - new super web-game" />
        <meta property="og:description" content="Что на этой странице? Будьте краткими" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="315" />
      </Helmet>
      {loading && <Loader className="loader-container--black" />}
      <div className="game-board-page__main" id="canvasdiv">
        <div
          style={{
            position: 'relative',
          }}
        >
          <canvas
            id="cnvmain"
            style={
              game
                ? {
                    backgroundSize: 'cover',
                    backgroundImage: `url(assets/images/boards/${preferencesService.getPreferences(
                      'BOARD'
                    )}/board/bg.png)`,
                  }
                : undefined
            }
          ></canvas>
          {game?.gameState.imPlaying() && <GameButtons gameState={game.gameState} />}
          {game?.gameState && <StatusMessage gameState={game.gameState} />}
        </div>
      </div>
      {game && <SideBar game={game} />}
      {game && opponenIdleActionsDialog && (
        <ModalOpponentIdleActions dialog={game.gameState.opponentIdleActionsDialog} />
      )}
    </div>
  );
}

const SideBar = (props: { game: GameBoard }) => {
  const gameState = props.game.gameState;
  const colors = [Color.White, Color.Black];
  const table = gameState.getTable();

  // Подписка для обновления компонента
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useSubject(gameState.onPlayersCountChanged, 0);
  useSubject(gameState.onTurnChanged);

  return (
    <aside className="game-sidebar">
      <GameMenu gameState={gameState} />
      <div className="game-status-menu">
        {colors.map((color) => (
          <PlayerInfo
            key={color.toString()}
            color={color}
            gameState={gameState}
            table={table}
            player={gameState.getPlayer(color)}
            icon={color === Color.Black ? blackChecker : whiteChecker}
          >
            <>
              <div>PIPS {gameState.getPips(color)}</div>
              <div>{gameState.getScore(color)} PT</div>
            </>
          </PlayerInfo>
        ))}
      </div>
      <MovesWindow
        movesHistory={gameState.getMoveHistory()}
        matchString={gameState.getTable().getMatchStringLong()}
        tickets={gameState.getTable().getTicketString()}
      />
      <GameChatLayout />
    </aside>
  );
};

async function initGameState(
  playersOnlineRepository: IPlayersOnlineRepository,
  imageProvider: ImageProvider<BackgammonImageCollectionKeys>,
  setGame: React.Dispatch<React.SetStateAction<GameBoard | undefined>>,
  board: string,
  preferencesService: IPreferencesService<Preferences>,
  loading: boolean,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (loading) return;
  setLoading(true);
  try {
    const gameState = playersOnlineRepository.me?.getTable()?.gameState;
    if (!gameState) throw new Error('Game state is not initialized!');
    await imageProvider.preloadImages(`boards/${board || 'classic'}`);
    const sizes = new Sizes();
    setGame(new GameBoard(gameState as GameState, imageProvider, preferencesService, sizes));
  } catch (e) {
    console.error("Can't resolve game modules:", e);
  } finally {
    setLoading(false);
  }
}
